import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import SEO from "../components/SEO";
import { Calculator, TrendingUp, Shield, Plus, Trash2, Share2, Copy, Check, Lock, X } from "lucide-react";
import api from "../lib/api";
import { useAuthStore } from "../store/authStore";

interface Protocol {
  id: number;
  name: string;
  slug: string;
  network: string;
  category: string;
  apy: number;
}

interface CalculatorEntry {
  id: number;
  protocolId: string;
  amount: number;
  compoundFrequency: "daily" | "monthly" | "yearly" | "none";
}

const COMPOUND_MULTIPLIERS = {
  daily: 365,
  monthly: 12,
  yearly: 1,
  none: 0,
};

function calcReturns(
  amount: number,
  apy: number,
  years: number,
  compound: string
) {
  const rate = apy / 100;
  const n = COMPOUND_MULTIPLIERS[compound as keyof typeof COMPOUND_MULTIPLIERS];
  let final;
  if (compound === "none") {
    final = amount + amount * rate * years;
  } else {
    final = amount * Math.pow(1 + rate / n, n * years);
  }
  return {
    total: final,
    earnings: final - amount,
    daily: (final - amount) / (years * 365),
    monthly: (final - amount) / (years * 12),
    yearly: (final - amount) / years,
  };
}

export default function CalculatorLandingPage() {
  const [protocols, setProtocols] = useState<Protocol[]>([]);
  const [loading, setLoading] = useState(true);
  const [years, setYears] = useState(1);
  const [entries, setEntries] = useState<CalculatorEntry[]>([
    { id: 1, protocolId: "", amount: 1000, compoundFrequency: "monthly" },
  ]);
  const [showLimitPrompt, setShowLimitPrompt] = useState(false);

  const { isAuthenticated, user } = useAuthStore();
  const tier = user?.subscriptionTier || "free";
  const maxEntries = !isAuthenticated ? 1 : tier === "free" ? 2 : 5;
  const maxYears = !isAuthenticated ? 2 : tier === "free" ? 5 : 10;

  useEffect(() => {
    api
      .get("/staking/options?limit=200")
      .then((res) => {
        const opts = res.data.data?.options || [];
        setProtocols(opts.filter((p: Protocol) => p.apy > 0));
        if (opts.length > 0) {
          setEntries((e) =>
            e.map((entry, i) =>
              i === 0 ? { ...entry, protocolId: String(opts[0].id) } : entry
            )
          );
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const addEntry = () => {
    if (entries.length >= maxEntries) {
      setShowLimitPrompt(true);
      return;
    }
    setEntries([
      ...entries,
      {
        id: Date.now(),
        protocolId: protocols[0] ? String(protocols[0].id) : "",
        amount: 1000,
        compoundFrequency: "monthly",
      },
    ]);
  };

  const removeEntry = (id: number) => {
    if (entries.length <= 1) return;
    setEntries(entries.filter((e) => e.id !== id));
    setShowLimitPrompt(false);
  };

  const updateEntry = (id: number, field: string, value: any) => {
    setEntries(
      entries.map((e) => (e.id === id ? { ...e, [field]: value } : e))
    );
  };

  const results = useMemo(() => {
    return entries
      .map((entry) => {
        const protocol = protocols.find(
          (p) => String(p.id) === entry.protocolId
        );
        if (!protocol) return null;
        const r = calcReturns(
          entry.amount,
          protocol.apy,
          years,
          entry.compoundFrequency
        );
        return { ...r, protocol, entry };
      })
      .filter(Boolean);
  }, [entries, protocols, years]);

  const totalInvested = entries.reduce((s, e) => s + e.amount, 0);
  const totalEarnings = results.reduce((s, r) => s + (r?.earnings || 0), 0);
  const totalFinal = totalInvested + totalEarnings;

  const [copied, setCopied] = useState(false);

  const shareText = useMemo(() => {
    if (results.length === 0) return "";
    return `I'd earn $${totalEarnings.toFixed(2)} staking $${totalInvested.toLocaleString()} over ${years} year${years > 1 ? "s" : ""} — check yours at residualvault.com/calculator`;
  }, [results, totalEarnings, totalInvested, years]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "My Staking Returns — ResidualVault",
          text: shareText,
          url: "https://residualvault.com/calculator",
        });
      } catch (_) {}
    } else {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const shareToTwitter = () => {
    window.open(
      `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`,
      "_blank"
    );
  };

  const colors = ["#3ecfcf", "#6c63ff", "#f59e0b", "#ef4444", "#10b981"];
  const allYears = [1, 2, 3, 5, 10];

  return (
    <div className="min-h-screen bg-navy-dark py-12">
      <SEO
        title="Crypto Staking Rewards Calculator — Live APY Rates"
        description="Calculate exactly how much you will earn from crypto staking. Compare 156+ protocols with real-time APY rates. See daily, monthly, and yearly staking rewards instantly."
        canonical="https://www.residualvault.com/calculator"
        keywords="crypto staking calculator, staking rewards calculator, DeFi yield calculator, staking APY calculator, compound interest crypto"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-4">
            <span className="text-cream">Crypto Staking </span>
            <span className="gradient-text-neon">Calculator</span>
          </h1>
          <p className="text-xl text-sky-blue max-w-3xl mx-auto">
            Calculate your potential staking earnings across 156+ protocols with
            live APY rates.
            {isAuthenticated && tier !== "free"
              ? " Compare up to 5 protocols side by side."
              : ""}
          </p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[
            {
              label: "Total invested",
              value: `$${totalInvested.toLocaleString()}`,
              color: "#888",
            },
            {
              label: `Total earnings (${years}yr)`,
              value: `$${totalEarnings.toFixed(2)}`,
              color: "#3ecfcf",
            },
            {
              label: "Total portfolio value",
              value: `$${totalFinal.toFixed(2)}`,
              color: "#6c63ff",
              gated: !isAuthenticated,
            },
          ].map((s) => (
            <div key={s.label} className="card-glass text-center relative">
              <div className="text-sky-blue text-sm uppercase tracking-wider mb-2">
                {s.label}
              </div>
              {s.gated ? (
                <>
                  <div className="text-4xl font-bold text-steel-blue/40 blur-sm select-none">
                    $12,345.67
                  </div>
                  <Link
                    to="/register"
                    className="absolute inset-0 flex items-center justify-center bg-navy-dark/40 rounded-xl"
                  >
                    <span className="flex items-center gap-1 text-neon-red text-sm font-semibold bg-navy-dark/80 px-3 py-1 rounded-lg">
                      <Lock className="w-4 h-4" /> Sign up free
                    </span>
                  </Link>
                </>
              ) : (
                <div
                  className="text-4xl font-bold"
                  style={{ color: s.color }}
                >
                  {s.value}
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Share results */}
        {isAuthenticated && results.length > 0 && totalEarnings > 0 && (
          <div className="card-glass mb-8 flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-sky-blue text-sm flex-1">{shareText}</p>
            <div className="flex gap-2">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2 bg-steel-blue/20 border border-sky-blue/30 rounded-lg text-sky-blue hover:bg-steel-blue/30 transition-all text-sm"
              >
                {copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Copy className="w-4 h-4" />
                )}
                {copied ? "Copied!" : "Copy"}
              </button>
              <button
                onClick={shareToTwitter}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-cta rounded-lg text-white font-semibold hover:opacity-90 transition-all text-sm"
              >
                <Share2 className="w-4 h-4" />
                Share on X
              </button>
            </div>
          </div>
        )}

        {/* Time period */}
        <div className="card-premium mb-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <h2 className="text-xl font-bold text-cream">Investment period</h2>
            <div className="flex gap-2">
              {allYears.map((y) => {
                const locked = y > maxYears;
                return (
                  <button
                    key={y}
                    onClick={() => !locked && setYears(y)}
                    className={`px-4 py-2 rounded-lg font-semibold transition-all relative ${
                      locked
                        ? "bg-steel-blue/10 text-sky-blue/30 cursor-not-allowed"
                        : years === y
                          ? "bg-gradient-cta text-white shadow-neon"
                          : "bg-steel-blue/20 text-sky-blue hover:bg-steel-blue/30"
                    }`}
                    disabled={locked}
                  >
                    {y}yr
                    {locked && (
                      <Lock className="w-3 h-3 absolute -top-1 -right-1 text-neon-red" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
          {!isAuthenticated && (
            <p className="text-sky-blue/60 text-xs mt-3">
              3yr, 5yr, and 10yr projections available with a free account.{" "}
              <Link to="/register" className="text-neon-red hover:underline">
                Sign up
              </Link>
            </p>
          )}
          {isAuthenticated && tier === "free" && (
            <p className="text-sky-blue/60 text-xs mt-3">
              10yr projections available on Starter plan.{" "}
              <Link to="/pricing#starter" className="text-neon-red hover:underline">
                Upgrade — $4.99/mo
              </Link>
            </p>
          )}
        </div>

        {/* Protocol entries */}
        <div className="space-y-4 mb-8">
          {entries.map((entry, idx) => {
            const result = results[idx];
            const protocol = protocols.find(
              (p) => String(p.id) === entry.protocolId
            );
            const color = colors[idx];

            return (
              <div
                key={entry.id}
                className="card-premium"
                style={{ borderLeft: `3px solid ${color}` }}
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-bold text-cream flex items-center gap-2">
                    <span style={{ color }}>&#9679;</span> Protocol {idx + 1}
                  </h3>
                  {entries.length > 1 && (
                    <button
                      onClick={() => removeEntry(entry.id)}
                      className="text-sky-blue/50 hover:text-neon-red transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="grid md:grid-cols-3 gap-4 mb-6">
                  <div>
                    <label className="text-sky-blue text-sm block mb-2">
                      Protocol
                    </label>
                    {loading ? (
                      <div className="h-11 bg-steel-blue/20 rounded-lg animate-pulse" />
                    ) : (
                      <select
                        value={entry.protocolId}
                        onChange={(e) =>
                          updateEntry(entry.id, "protocolId", e.target.value)
                        }
                        className="w-full bg-navy-dark border border-sky-blue/30 rounded-lg px-3 py-2.5 text-cream focus:outline-none focus:border-neon-red transition-colors"
                      >
                        {protocols.map((p) => (
                          <option
                            key={p.id}
                            value={String(p.id)}
                            className="bg-navy-dark"
                          >
                            {p.name} ({p.network}) —{" "}
                            {parseFloat(String(p.apy)).toFixed(2)}% APY
                          </option>
                        ))}
                      </select>
                    )}
                  </div>
                  <div>
                    <label className="text-sky-blue text-sm block mb-2">
                      Amount (USD)
                    </label>
                    <div className="flex items-center gap-2">
                      <span className="text-sky-blue">$</span>
                      <input
                        type="number"
                        value={entry.amount}
                        onChange={(e) =>
                          updateEntry(entry.id, "amount", Number(e.target.value))
                        }
                        className="flex-1 bg-navy-dark border border-sky-blue/30 rounded-lg px-3 py-2.5 text-cream focus:outline-none focus:border-neon-red transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-sky-blue text-sm block mb-2">
                      Compounding
                    </label>
                    <select
                      value={entry.compoundFrequency}
                      onChange={(e) =>
                        updateEntry(
                          entry.id,
                          "compoundFrequency",
                          e.target.value
                        )
                      }
                      className="w-full bg-navy-dark border border-sky-blue/30 rounded-lg px-3 py-2.5 text-cream focus:outline-none focus:border-neon-red transition-colors"
                    >
                      <option value="daily" className="bg-navy-dark">
                        Daily compound
                      </option>
                      <option value="monthly" className="bg-navy-dark">
                        Monthly compound
                      </option>
                      <option value="yearly" className="bg-navy-dark">
                        Yearly compound
                      </option>
                      <option value="none" className="bg-navy-dark">
                        No compounding
                      </option>
                    </select>
                  </div>
                </div>

                {result && protocol && (
                  <div className="bg-navy-dark/50 rounded-xl p-4 border border-sky-blue/10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center">
                        <div className="text-sky-blue text-xs mb-1">APY</div>
                        <div className="text-2xl font-bold gradient-text-neon">
                          {parseFloat(String(protocol.apy)).toFixed(2)}%
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sky-blue text-xs mb-1">
                          Daily earnings
                        </div>
                        <div className="text-2xl font-bold text-cream">
                          ${result.daily.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-center">
                        <div className="text-sky-blue text-xs mb-1">
                          Monthly earnings
                        </div>
                        <div className="text-2xl font-bold text-cream">
                          ${result.monthly.toFixed(2)}
                        </div>
                      </div>
                      <div className="text-center relative">
                        <div className="text-sky-blue text-xs mb-1">
                          {years}yr total value
                        </div>
                        {!isAuthenticated ? (
                          <>
                            <div className="text-2xl font-bold text-steel-blue/40 blur-sm select-none">
                              $12,345
                            </div>
                            <Link
                              to="/register"
                              className="absolute inset-0 flex items-center justify-center"
                            >
                              <span className="flex items-center gap-1 text-neon-red text-xs font-semibold">
                                <Lock className="w-3 h-3" /> Sign up
                              </span>
                            </Link>
                          </>
                        ) : (
                          <div
                            className="text-2xl font-bold"
                            style={{ color }}
                          >
                            ${result.total.toFixed(2)}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Add protocol button / limit prompt */}
        {entries.length < maxEntries && !showLimitPrompt ? (
          <button
            onClick={addEntry}
            className="w-full py-4 border-2 border-dashed border-sky-blue/30 rounded-xl text-sky-blue hover:border-neon-red hover:text-neon-red transition-all flex items-center justify-center gap-2 mb-8"
          >
            <Plus className="w-5 h-5" />
            Add another protocol to compare (up to {maxEntries})
          </button>
        ) : (
          <div className="w-full mb-8">
            {showLimitPrompt && !isAuthenticated && (
              <div className="p-5 border-2 border-dashed border-neon-red/30 rounded-xl bg-gradient-to-r from-neon-red/5 to-electric-blue/5 relative">
                <button
                  onClick={() => setShowLimitPrompt(false)}
                  className="absolute top-3 right-3 text-sky-blue/50 hover:text-cream transition"
                >
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-lg font-bold text-cream mb-2">
                  Sign up free to model 2 protocols — or upgrade to Starter for 5 protocols and full 10-year projections.
                </h3>
                <div className="flex gap-3 mt-3">
                  <Link
                    to="/register"
                    className="btn-neon inline-flex items-center gap-2 px-5 py-2"
                  >
                    Sign Up Free
                  </Link>
                  <Link
                    to="/pricing"
                    className="bg-steel-blue/20 border border-sky-blue/30 text-sky-blue font-semibold px-5 py-2 rounded-lg hover:bg-steel-blue/30 transition-all"
                  >
                    See Plans
                  </Link>
                </div>
              </div>
            )}

            {showLimitPrompt && isAuthenticated && tier === "free" && (
              <div className="p-5 border-2 border-dashed border-neon-red/30 rounded-xl bg-gradient-to-r from-neon-red/5 to-electric-blue/5 relative">
                <button
                  onClick={() => setShowLimitPrompt(false)}
                  className="absolute top-3 right-3 text-sky-blue/50 hover:text-cream transition"
                >
                  <X className="w-4 h-4" />
                </button>
                <h3 className="text-lg font-bold text-cream mb-2">
                  Upgrade to Starter ($4.99/mo) to model up to 5 protocols simultaneously and unlock all time horizons up to 10 years.
                </h3>
                <div className="flex gap-3 mt-3">
                  <Link
                    to="/pricing#starter"
                    className="btn-neon inline-flex items-center gap-2 px-5 py-2"
                  >
                    Upgrade to Starter
                  </Link>
                </div>
              </div>
            )}

            {!showLimitPrompt && !isAuthenticated && entries.length >= maxEntries && (
              <button
                onClick={() => setShowLimitPrompt(true)}
                className="block w-full py-4 border-2 border-dashed border-neon-red/30 rounded-xl text-center transition-all hover:border-neon-red/60 bg-gradient-to-r from-neon-red/5 to-electric-blue/5"
              >
                <div className="flex items-center justify-center gap-2 text-neon-red font-semibold">
                  <Lock className="w-5 h-5" />
                  Sign up free to compare multiple protocols side by side
                </div>
              </button>
            )}

            {!showLimitPrompt && isAuthenticated && tier === "free" && entries.length >= maxEntries && (
              <button
                onClick={() => setShowLimitPrompt(true)}
                className="block w-full py-4 border-2 border-dashed border-neon-red/30 rounded-xl text-center transition-all hover:border-neon-red/60 bg-gradient-to-r from-neon-red/5 to-electric-blue/5"
              >
                <div className="flex items-center justify-center gap-2 text-neon-red font-semibold">
                  <Plus className="w-5 h-5" />
                  Upgrade to Starter to compare up to 5 protocols — $4.99/mo
                </div>
              </button>
            )}
          </div>
        )}

        {/* Comparison table — only for authenticated */}
        {results.length > 1 && isAuthenticated && (
          <div className="card-premium mb-8">
            <h2 className="text-2xl font-bold text-cream mb-6">
              Side-by-side comparison
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-sky-blue/20">
                    <th className="text-left py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      Protocol
                    </th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      APY
                    </th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      Invested
                    </th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      Daily
                    </th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      Monthly
                    </th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      {years}yr Earnings
                    </th>
                    <th className="text-right py-3 px-4 text-sky-blue text-sm uppercase tracking-wider">
                      {years}yr Total
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {results.map(
                    (r, i) =>
                      r && (
                        <tr
                          key={i}
                          className="border-b border-sky-blue/10 hover:bg-steel-blue/10 transition-colors"
                        >
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <span style={{ color: colors[i] }}>&#9679;</span>
                              <span className="text-cream font-medium">
                                {r.protocol.name}
                              </span>
                              <span className="text-sky-blue text-xs">
                                ({r.protocol.network})
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-right gradient-text-neon font-bold">
                            {parseFloat(String(r.protocol.apy)).toFixed(2)}%
                          </td>
                          <td className="py-3 px-4 text-right text-cream">
                            ${r.entry.amount.toLocaleString()}
                          </td>
                          <td className="py-3 px-4 text-right text-cream">
                            ${r.daily.toFixed(2)}
                          </td>
                          <td className="py-3 px-4 text-right text-cream">
                            ${r.monthly.toFixed(2)}
                          </td>
                          <td
                            className="py-3 px-4 text-right"
                            style={{ color: colors[i] }}
                          >
                            ${r.earnings.toFixed(2)}
                          </td>
                          <td
                            className="py-3 px-4 text-right font-bold"
                            style={{ color: colors[i] }}
                          >
                            ${r.total.toFixed(2)}
                          </td>
                        </tr>
                      )
                  )}
                  <tr className="bg-steel-blue/10">
                    <td colSpan={2} className="py-3 px-4 text-cream font-bold">
                      Total
                    </td>
                    <td className="py-3 px-4 text-right text-cream font-bold">
                      ${totalInvested.toLocaleString()}
                    </td>
                    <td colSpan={2} />
                    <td className="py-3 px-4 text-right text-neon-red font-bold">
                      ${totalEarnings.toFixed(2)}
                    </td>
                    <td className="py-3 px-4 text-right text-neon-red font-bold">
                      ${totalFinal.toFixed(2)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Info cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          <div className="card-premium text-center">
            <div className="w-16 h-16 bg-gradient-cta rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon">
              <Calculator className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">
              156+ Protocols
            </h3>
            <p className="text-sky-blue">
              Calculate returns for Cosmos ATOM, Ethereum, Solana, Polkadot, and
              156+ more staking protocols with live APY rates.
            </p>
          </div>
          <div className="card-premium text-center">
            <div className="w-16 h-16 bg-gradient-cta rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">
              Compound Interest
            </h3>
            <p className="text-sky-blue">
              See how daily, monthly, or yearly compounding affects your returns
              over 1, 2, 3, 5, and 10 year horizons.
            </p>
          </div>
          <div className="card-premium text-center">
            <div className="w-16 h-16 bg-gradient-cta rounded-full flex items-center justify-center mx-auto mb-4 shadow-neon">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-xl font-bold text-cream mb-2">
              Compare Side by Side
            </h3>
            <p className="text-sky-blue">
              {!isAuthenticated
                ? "Sign up free to compare multiple protocols and find the best combination for your portfolio."
                : "Add multiple protocols and compare them side by side. Find the best combination for your staking portfolio."}
            </p>
          </div>
        </div>

        {/* CTA */}
        <div className="card-premium text-center">
          <h2 className="text-3xl font-bold mb-4">
            <span className="text-cream">Ready to start </span>
            <span className="gradient-text-neon">earning?</span>
          </h2>
          <p className="text-sky-blue mb-6">
            Compare all 156 protocols and find the best staking rewards for your
            portfolio
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <Link
              to="/staking"
              className="btn-neon inline-flex items-center"
            >
              Browse all protocols
            </Link>
            {!isAuthenticated && (
              <Link
                to="/register"
                className="bg-gradient-to-r from-neon-red to-electric-blue text-white font-semibold px-6 py-3 rounded-lg hover:shadow-neon transition-all inline-flex items-center"
              >
                Create free account
              </Link>
            )}
            {isAuthenticated && tier === "free" && (
              <Link
                to="/pricing#starter"
                className="bg-gradient-to-r from-neon-red to-electric-blue text-white font-semibold px-6 py-3 rounded-lg hover:shadow-neon transition-all inline-flex items-center"
              >
                Upgrade from $4.99/mo
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
